import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from 'react';
import { reportRenderError } from './error-reporting';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * `page`: a interface inteira caiu (ocupa a tela). `panel`: só o painel do chat caiu e a lista
   * ao lado continua em pé (a mensagem fica dentro do <main> da casca).
   */
  scope?: 'page' | 'panel';
  /** Quando muda, uma falha anterior é esquecida (ex.: outra conversa aberta limpa o painel). */
  resetKey?: string;
}

interface ErrorBoundaryState {
  failed: boolean;
}

/**
 * Sem um boundary, uma exceção de renderização desmonta a árvore inteira do React 19 e deixa a
 * página em branco, sem mensagem e sem o botão "Sair". Aqui a pessoa vê o que fazer. Nada da
 * exceção (mensagem, pilha, dados) vai para a tela; o console recebe só o nome do erro.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportRenderError(error, info.componentStack);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return <Fallback scope={this.props.scope ?? 'page'} />;
  }
}

function Fallback({ scope }: { scope: 'page' | 'panel' }) {
  const reloadRef = useRef<HTMLButtonElement>(null);

  // O que estava focado saiu da tela junto com a falha e o foco foi para o <body>; leva-o ao
  // botão. Se o foco está em outro lugar (a lista, num painel que quebrou), não o rouba.
  useEffect(() => {
    const active = document.activeElement;
    if (!active || active === document.body) reloadRef.current?.focus();
  }, []);

  const content = (
    <>
      <p role="alert" className={styles.message}>
        Algo deu errado.
      </p>
      <p className={styles.hint}>Recarregue a página para continuar.</p>
      <button
        type="button"
        ref={reloadRef}
        className={styles.reload}
        onClick={() => window.location.reload()}
      >
        Recarregar a página
      </button>
    </>
  );

  // Um <main> só existe quando a falha é geral; no painel já há um <main> em volta.
  return scope === 'page' ? (
    <main className={`${styles.fallback} ${styles.page}`}>{content}</main>
  ) : (
    <div className={styles.fallback}>{content}</div>
  );
}
